import { useState } from "react";
import { Roles } from "../../../../interface/interfaces";

interface RoleDetailsModalProps {
  selectedRole: Roles | null;
  isModalOpen: boolean;
  onClose: () => void;
}

const RoleDetailsModal = ({
  selectedRole,
  isModalOpen,
  onClose,
}: RoleDetailsModalProps) => {
  const [expandedPermissions, setExpandedPermissions] = useState<{
    [key: string]: boolean;
  }>({});

  if (!selectedRole || !isModalOpen) return null;

  const togglePermissions = (type: string) => {
    setExpandedPermissions((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const statusColors = {
      ACTIVE: "bg-green-100 text-green-800 border-green-300",
      INACTIVE: "bg-red-100 text-red-800 border-red-300",
      PENDING: "bg-yellow-100 text-yellow-800 border-yellow-300",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium border ${
          statusColors[status as keyof typeof statusColors] ||
          "bg-gray-100 text-gray-800 border-gray-300"
        }`}
      >
        {status}
      </span>
    );
  };

  const RoleTypesBadges = ({ role }: { role: Roles }) => {
    const types = [];
    if (role.isAdminRole)
      types.push({
        label: "Admin",
        color: "bg-blue-100 text-blue-800 border-blue-300",
      });
    if (role.isSystemRole)
      types.push({
        label: "System",
        color: "bg-purple-100 text-purple-800 border-purple-300",
      });
    if (role.isCustomRole)
      types.push({
        label: "Custom",
        color: "bg-green-100 text-green-800 border-green-300",
      });
    if (role.isMerchantRole)
      types.push({
        label: "Merchant",
        color: "bg-orange-100 text-orange-800 border-orange-300",
      });

    return (
      <div className="flex flex-wrap gap-1">
        {types.map((type, index) => (
          <span
            key={index}
            className={`px-2 py-1 rounded text-xs font-medium border ${type.color}`}
          >
            {type.label}
          </span>
        ))}
        {types.length === 0 && (
          <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300">
            None
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {selectedRole.roleName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Role Name
                  </label>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedRole.roleName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Description
                  </label>
                  <p className="text-sm text-gray-700">
                    {selectedRole.roleDescription || "No description"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Status
                  </label>
                  <div className="mt-1">
                    <StatusBadge status={selectedRole.roleStatus} />
                  </div>
                </div>
                {selectedRole.merchantName && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Merchant
                    </label>
                    <p className="text-sm text-gray-700">
                      {selectedRole.merchantName}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Role Types */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Role Types
              </h3>
              <RoleTypesBadges role={selectedRole} />
            </div>

            {/* Permissions Section */}
            <div className="md:col-span-2 bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Permissions
              </h3>
              <div className="space-y-4">
                {/* Role Permissions */}
                <div>
                  <button
                    onClick={() => togglePermissions("role")}
                    className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium">
                      Role Permissions (
                      {selectedRole.rolePermissions?.length || 0})
                    </span>
                    <span className="text-xl">
                      {expandedPermissions.role ? "−" : "+"}
                    </span>
                  </button>
                  {expandedPermissions.role && (
                    <div className="mt-2 p-4 bg-white border border-gray-200 rounded-lg">
                      {selectedRole.rolePermissions?.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {selectedRole.rolePermissions.map(
                            (permission, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 text-sm text-gray-700"
                              >
                                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                                <span>{permission}</span>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          No role permissions assigned
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Admin Permissions */}
                <div>
                  <button
                    onClick={() => togglePermissions("admin")}
                    className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium">
                      Admin Permissions (
                      {selectedRole.adminRolePermissions?.length || 0})
                    </span>
                    <span className="text-xl">
                      {expandedPermissions.admin ? "−" : "+"}
                    </span>
                  </button>
                  {expandedPermissions.admin && (
                    <div className="mt-2 p-4 bg-white border border-gray-200 rounded-lg">
                      {selectedRole.adminRolePermissions?.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {selectedRole.adminRolePermissions.map(
                            (permission, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 text-sm text-gray-700"
                              >
                                <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></div>
                                <span>{permission}</span>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          No admin permissions assigned
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleDetailsModal;
